import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { X, Check, Plus, Ban } from "lucide-react";
import { AlertBox } from "./AlertBox";
import { Checkbox } from "../UI/checkbox";
import { Label } from "../UI/label";
import { Textarea } from "../UI/textarea";
import { Button } from "../UI/button";
import { toast } from "sonner";

import { getMyAgenciesMappedIDs } from "../../Utils/Utilities";
import { getAuthData } from "../../Utils/AuthUtils";
import { SpeechControls } from "./SpeechControls";
import { cn } from "../../Lib/Cn";

function CollapsibleDescription({ description, title }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        setIsExpanded(false);
    }, [description]);

    useLayoutEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight + 2);
        }
    }, [description, isExpanded]);

    if (!description || description === title) return null;

    return (
        <div className="mb-2 text-left">
            <p
                ref={textRef}
                onClick={() => { if (isOverflowing || isExpanded) setIsExpanded(!isExpanded); }}
                className={cn(
                    "text-text-color whitespace-pre-wrap text-sm break-words transition-all duration-200",
                    (isOverflowing || isExpanded) ? "cursor-pointer select-text" : "cursor-text",
                    !isExpanded && "line-clamp-3 overflow-hidden"
                )}
            >
                {description}
            </p>
            {(isOverflowing || isExpanded) && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-xs font-semibold text-sahi-primary-color hover:underline mt-1 cursor-pointer focus:outline-none inline-block"
                >
                    {isExpanded ? "See less" : "See more"}
                </button>
            )}
        </div>
    );
}

// Shared confirmation dialog for both completing and cancelling a task.
// `mode` picks which flow renders: "complete" (default) or "cancel".
export function TaskCompleteDialog({
    open,
    onOpenChange,
    task,
    onAction,
    className,
    mode = "complete",
    shareByWhatsApp = false,
    onWhatsAppShareChange = null,
    initialStatusNote = "",
    forceShowNoteField = false
}) {
    const repeatAfter = Number(task?.RepeatAfter ?? task?.repeatAfter ?? 0);
    const repeatDT = task?.RepeatDT ?? task?.repeatDT ?? null;
    const isRepeatable = repeatAfter > 0 || Boolean(repeatDT);

    const isDecisionTask = Number(task?.Class ?? task?.class ?? task?.taskClass ?? task?.TaskClass ?? 0) === 3;

    const [repeat, setRepeat] = useState(isRepeatable);
    const [statusNote, setStatusNote] = useState("");
    const [showNoteField, setShowNoteField] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (open) {
            setRepeat(isRepeatable);
            setStatusNote(initialStatusNote || "");
            const isMeeting = Number(task?.Class ?? task?.class ?? task?.taskClass ?? task?.TaskClass ?? 0) === 2;
            setShowNoteField(isDecisionTask || isMeeting || Boolean(initialStatusNote) || forceShowNoteField);
        }
    }, [open, isRepeatable, initialStatusNote, forceShowNoteField, isDecisionTask, task]);

    useEffect(() => {
        if (open && showNoteField) {
            const timer = setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [open, showNoteField]);

    const title = task?.Ttl || task?.ttl || task?.title || task?.taskName || task?.Task || "";
    const description = task?.Txt || task?.txt || task?.description || "";
    const connID = task?.ConnID ?? task?.connID;
    const bizName = task?.BizName || task?.bizName || task?.connectionData?.bizName || task?.connectionData?.BizName;
    const contactName = task?.ContactName || task?.contactName || task?.connectionData?.contactName || task?.connectionData?.ContactName;
    const hasValidConnection = connID != 0 && connID != null && (bizName || contactName);

    const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
    const authData = getAuthData();
    const currentUserID = authData?.UserID ? Number(authData.UserID) : 0;
    const assigneeID = Number(task?.uID || task?.pocUserID || task?.POCUserID || 0);
    const creatorID = Number(task?.createdUserID || task?.CreatedUserID || task?.CreatedUID || 0);
    const isAssigneeMe = (assigneeID === 0) || (assigneeID === currentUserID) || myUserIDs.includes(assigneeID);
    const isCreatorMe = (creatorID === 0) || (creatorID === currentUserID) || myUserIDs.includes(creatorID);
    const isSelfTask = isAssigneeMe && isCreatorMe;
    const showWhatsAppOption = !isSelfTask && onWhatsAppShareChange !== null;

    if (mode === "cancel" || mode === "reject") {
        const isReject = mode === "reject";
        const taskClass = Number(task?.Class ?? task?.class ?? task?.taskClass ?? task?.TaskClass ?? 1);
        const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
        const isMeeting = taskClass === 2;
        const dialogTitle = isMeeting ? (isReject ? "Decline Meeting" : "Cancel Meeting") : (isReject ? `Reject ${classLabel}` : `Cancel ${classLabel}`);
        const confirmMessage = isMeeting ? (isReject ? "Are you sure you want to decline this meeting?" : "Are you sure you want to cancel this meeting?") : `Are you sure you want to ${isReject ? "reject" : "cancel"} this ${classLabel.toLowerCase()}?`;
        const noteLabel = isMeeting ? (isReject ? "Reason to not attend" : "Cancellation Reason") : (isReject ? `${classLabel} Rejection Note` : `${classLabel} Cancellation Note`);
        const placeholderText = isMeeting ? (isReject ? "e.g. Out of office, client conflict, etc." : "e.g. Venue changed, meeting postponed, etc.") : "e.g. Deal closed or client not interested.";
        return (
            <AlertBox
                open={open}
                onOpenChange={onOpenChange}
                title={dialogTitle}
                mobileRow={true}
                className={className}
                content={
                    <div className="space-y-3">
                        <p className="my-2"><span className="font-semibold text-md">{title}</span></p>
                        <CollapsibleDescription description={description} title={title} />
                        <p className="text-text-color">{confirmMessage}</p>
                        {!showNoteField ? (
                            <div className="text-left w-full">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground px-0 hover:text-primary h-auto py-1"
                                    onClick={() => setShowNoteField(true)}
                                >
                                    +<span className="underline underline-offset-4">Add {isMeeting ? "reason" : (isReject ? "rejection" : "cancellation")} note</span>
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-1.5 text-left w-full">
                                <Label className="cursor-pointer">
                                    {noteLabel}
                                </Label>
                                <div className="relative w-full">
                                    <Textarea
                                        id="cancel-note-textarea"
                                        value={statusNote}
                                        onChange={(e) => setStatusNote(e.target.value)}
                                        placeholder={placeholderText}
                                        allowResize={false}
                                        className="min-h-20 text-sm pr-20"
                                        autoFocus
                                    />
                                    <SpeechControls
                                        targetId="cancel-note-textarea"
                                        value={statusNote}
                                        onChange={(val) => setStatusNote(val)}
                                        isMobile={window.innerWidth < 1024}
                                    />
                                </div>
                            </div>
                        )}
                        {showWhatsAppOption && (
                            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none text-left w-full justify-start">
                                <Checkbox checked={shareByWhatsApp} onCheckedChange={(v) => onWhatsAppShareChange(v === true)} />
                                <span className="text-sm text-text-color">Send by WhatsApp</span>
                            </label>
                        )}
                    </div>
                }
                actions={[
                    {
                        label: "No",
                        value: "no",
                        variant: "cancel",
                        icon: <X className="h-4 w-4" />,
                        className: "bg-sahi-green border hover:border-sahi-green hover:text-sahi-green text-primary-foreground"
                    },
                    {
                        label: "Yes",
                        value: "yes",
                        icon: <Ban className="h-4 w-4" />,
                        className: "bg-sahi-red border hover:border-sahi-red hover:text-sahi-red text-primary-foreground"
                    },
                ]}
                onAction={(value) => {
                    onAction && onAction(value, statusNote);
                }}
            />
        );
    }

    const isApprovalTask = Number(task?.Class ?? task?.class ?? task?.taskClass ?? task?.TaskClass ?? 0) === 4;
    const taskClass = Number(task?.Class ?? task?.class ?? task?.taskClass ?? task?.TaskClass ?? 1);
    const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";

    return (
        <AlertBox
            open={open}
            onOpenChange={onOpenChange}
            title={isApprovalTask ? "Approval Decision" : (isDecisionTask ? "Your Decision" : `Complete ${classLabel}`)}
            mobileRow={!hasValidConnection}
            mobileStack={hasValidConnection}
            className={className}
            content={
                <div className="space-y-3">
                    <p className="my-2"><span className="font-semibold text-md">{title}</span></p>
                    <CollapsibleDescription description={description} title={title} />
                    {hasValidConnection && !isApprovalTask && (
                        <p className="text-text-color">
                            Do you want to add a record for this completed {classLabel.toLowerCase()}?
                        </p>
                    )}
                    {!showNoteField ? (
                        <div className="text-left w-full">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground px-0 hover:text-primary h-auto py-1"
                                onClick={() => setShowNoteField(true)}
                            >
                                +<span className="underline underline-offset-4">Add completion note</span>
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-1.5 text-left w-full">
                            <Label className="cursor-pointer">
                                {isApprovalTask ? "Approval Remark" : (isDecisionTask ? "Decision" : "Completion Note")}
                            </Label>
                            <div className="relative w-full">
                                <Textarea
                                    ref={textareaRef}
                                    id="completion-note-textarea"
                                    value={statusNote}
                                    onChange={(e) => setStatusNote(e.target.value)}
                                    placeholder={isApprovalTask ? "e.g. Approved because of correct documents." : (isDecisionTask ? "e.g. Approved the budget increase." : "e.g. Done with meeting, waiting for response.")}
                                    allowResize={false}
                                    className="min-h-20 text-sm pr-20"
                                    autoFocus
                                />
                                <SpeechControls
                                    targetId="completion-note-textarea"
                                    value={statusNote}
                                    onChange={(val) => setStatusNote(val)}
                                    isMobile={window.innerWidth < 1024}
                                />
                            </div>
                        </div>
                    )}
                    {isRepeatable && !isApprovalTask && (
                        <label className="flex items-center gap-2 mt-1 cursor-pointer select-none">
                            <Checkbox checked={repeat} onCheckedChange={(v) => setRepeat(v === true)} />
                            <span className="text-sm text-text-color">Repeat this {classLabel.toLowerCase()}</span>
                        </label>
                    )}
                    {showWhatsAppOption && (
                        <label className="flex items-center gap-2 mt-2 cursor-pointer select-none text-left w-full justify-start">
                            <Checkbox checked={shareByWhatsApp} onCheckedChange={(v) => onWhatsAppShareChange(v === true)} />
                            <span className="text-sm text-text-color">Send by WhatsApp</span>
                        </label>
                    )}
                </div>
            }
            actions={[
                {
                    label: "Cancel",
                    variant: "cancel",
                    value: "cancel",
                    icon: <X className="h-4 w-4" />,
                    className: isApprovalTask
                        ? "sahi-secondary-button sm:flex-none sm:min-w-[100px]"
                        : "bg-sahi-red border hover:border-sahi-red hover:text-sahi-red text-primary-foreground"
                },
                {
                    label: isApprovalTask
                        ? (initialStatusNote === 'Approved' ? 'Approve' : (initialStatusNote === 'Disapproved' ? 'Disapprove' : initialStatusNote || "Submit"))
                        : (hasValidConnection ? "No, Just Complete" : "Complete"),
                    value: "complete",
                    icon: (isApprovalTask && initialStatusNote === "Disapproved")
                        ? <Ban className="h-4 w-4" />
                        : <Check className="h-4 w-4" />,
                    className: (isApprovalTask && initialStatusNote === "Disapproved")
                        ? "bg-sahi-red border hover:border-sahi-red hover:text-sahi-red text-primary-foreground hover:bg-transparent sm:flex-none sm:min-w-[110px]"
                        : "sahi-primary-button sm:flex-none sm:min-w-[110px]"
                },
                ...(hasValidConnection && !isApprovalTask
                    ? [{
                        label: "Yes, Add Record",
                        value: "record",
                        icon: <Plus className="h-4 w-4" />,
                        className: "sahi-primary-button sm:flex-none sm:min-w-[110px]"
                    }]
                    : [])
            ]}
            onAction={(value) => {
                if (isDecisionTask && (value === "complete" || value === "record")) {
                    if (!statusNote.trim()) {
                        toast.error("Decision note is compulsory.");
                        return;
                    }
                }
                if (isApprovalTask && !initialStatusNote && (value === "complete" || value === "record")) {
                    if (!statusNote.trim()) {
                        toast.error("Approval note is compulsory for custom decisions.");
                        return;
                    }
                }
                onAction && onAction(value, repeat, statusNote);
            }}
        />
    );
}
